'use client';

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSDKStore } from '@/lib/sdk/store';
import { SDK_SCOPES, type CreateSDKKeyInput } from '@/lib/sdk/types';
import { Copy, Check, Eye, EyeOff, AlertTriangle, Key } from 'lucide-react';

interface CreateKeyDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CreateKeyDialog({ open, onClose }: CreateKeyDialogProps) {
  const { createKey, newKeyValue, clearNewKeyValue, keysLoading } = useSDKStore();

  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['agent:read']);
  const [expiresIn, setExpiresIn] = useState<string>('never');
  const [rateLimit, setRateLimit] = useState<string>('60');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (selectedScopes.length === 0) {
      setError('At least one scope is required');
      return;
    }

    setError(null);

    try {
      const input: CreateSDKKeyInput = {
        name,
        scopes: selectedScopes,
        rate_limit_per_minute: parseInt(rateLimit) || 60,
      };

      if (expiresIn !== 'never') {
        input.expires_in_days = parseInt(expiresIn);
      }

      await createKey(input);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleClose = () => {
    setName('');
    setSelectedScopes(['agent:read']);
    setExpiresIn('never');
    setRateLimit('60');
    setError(null);
    setCopied(false);
    setShowKey(false);
    clearNewKeyValue();
    onClose();
  };

  const handleCopy = () => {
    if (newKeyValue) {
      navigator.clipboard.writeText(newKeyValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleScope = (scopeId: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scopeId)
        ? prev.filter((s) => s !== scopeId)
        : [...prev, scopeId]
    );
  };

  // Group scopes by category
  const scopesByCategory = SDK_SCOPES.reduce((acc, scope) => {
    if (!acc[scope.category]) acc[scope.category] = [];
    acc[scope.category].push(scope);
    return acc;
  }, {} as Record<string, typeof SDK_SCOPES>);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="size-5" />
            {newKeyValue ? 'API Key Created' : 'Create API Key'}
          </DialogTitle>
          <DialogDescription>
            {newKeyValue
              ? 'Save this key now - it will not be shown again!'
              : 'Create a new API key for SDK or CLI access'}
          </DialogDescription>
        </DialogHeader>

        {newKeyValue ? (
          // Show the new key
          <div className="space-y-4 py-4">
            <Alert>
              <AlertTriangle className="size-4" />
              <AlertDescription>
                <strong>Important:</strong> Copy this key now. You won't be able to see it again!
              </AlertDescription>
            </Alert>

            <div>
              <Label>Your API Key</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={showKey ? newKeyValue : '••••••••••••••••••••••••••••••••'}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button variant="ghost" size="icon" onClick={() => setShowKey(!showKey)}>
                  {showKey ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={handleCopy}>
                  {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          // Create form
          <>
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="size-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label htmlFor="key-name">Name</Label>
                <Input
                  id="key-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My SDK Key"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Scopes</Label>
                <div className="mt-2 space-y-4 border rounded-lg p-4 max-h-[200px] overflow-y-auto">
                  {Object.entries(scopesByCategory).map(([category, scopes]) => (
                    <div key={category}>
                      <h4 className="text-sm font-medium mb-2">{category}</h4>
                      <div className="space-y-2">
                        {scopes.map((scope) => (
                          <div key={scope.id} className="flex items-start space-x-2">
                            <Checkbox
                              id={scope.id}
                              checked={selectedScopes.includes(scope.id)}
                              onCheckedChange={() => toggleScope(scope.id)}
                            />
                            <div className="grid gap-0.5">
                              <Label
                                htmlFor={scope.id}
                                className="text-sm font-normal cursor-pointer"
                              >
                                {scope.name}
                              </Label>
                              <p className="text-xs text-muted-foreground">
                                {scope.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Expiration</Label>
                  <Select value={expiresIn} onValueChange={setExpiresIn}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="never">Never</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                      <SelectItem value="365">1 year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Rate Limit (req/min)</Label>
                  <Input
                    type="number"
                    value={rateLimit}
                    onChange={(e) => setRateLimit(e.target.value)}
                    min="1"
                    max="1000"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={keysLoading}>
                {keysLoading ? 'Creating...' : 'Create API Key'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
